import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { BonusCard } from './components/BonusCard';
import { Footer } from './components/Footer';
import { CheckIcon, Heart, Star, ShieldCheck, Sparkles, BookOpen } from 'lucide-react';
// Simplified animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function App() {
  return <div className="min-h-screen bg-[#F7F3EF] text-[#3A3A3A] font-sans selection:bg-[#E8CFCF] selection:text-[#3A3A3A]" data-id="element-0">
      {/* HERO SECTION */}
      <header className="pt-20 pb-16 px-4 md:pt-32 md:pb-24 bg-white border-b border-[#E8CFCF]/40" data-id="element-1">
        <div className="max-w-5xl mx-auto text-center" data-id="element-2">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-8" data-id="element-3">
            <motion.div variants={fadeInUp} className="flex justify-center" data-id="element-4">
              <span className="inline-flex items-center gap-3 py-3 px-8 rounded-full bg-[#F5EAEA] text-[#D88C7A] text-lg md:text-xl font-bold border-2 border-[#E8CFCF] shadow-md" data-id="element-5">
                <Sparkles className="w-6 h-6" data-id="element-6" />
                APRENDE A MANEJAR BERRINCHES SIN CULPA NI GRITOS
              </span>
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-4xl md:text-6xl font-bold text-[#3A3A3A] leading-[1.1] tracking-tight max-w-4xl mx-auto" data-id="element-7">
              Deja de sentir culpa cada vez que pierdes la paciencia con tu hijo
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-[#3A3A3A]/70 max-w-3xl mx-auto leading-relaxed" data-id="element-8">
              Gritas… y después te sientes mal. Prometes hacerlo mejor… pero
              vuelve a pasar. No es que seas una mala mamá. Es que nadie te
              enseñó cómo manejar estos momentos.
            </motion.p>

            <motion.p variants={fadeInUp} className="text-base md:text-lg text-[#D88C7A] font-semibold max-w-2xl mx-auto" data-id="element-9">
              👉 Acceso inmediato + materiales imprimibles incluidos
            </motion.p>

            <motion.div variants={fadeInUp} className="flex justify-center pt-6 pb-4" data-id="element-10">
              <img src="https://cdn.magicpatterns.com/uploads/22KC9ppymGVVBFRz2eAabV/portada.jpg" alt="Guía de Crianza con Inteligencia Emocional" className="w-full max-w-md rounded-2xl shadow-2xl" data-id="element-11" />
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4" data-id="element-12">
              <a href="https://crianza.xn--amordemam-81a.com/products/guia-de-crianza-con-inteligencia-emocional" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto bg-[#D88C7A] hover:bg-[#C57665] text-white font-bold text-lg py-4 px-8 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 text-center" data-id="element-13">
                QUIERO DEJAR DE GRITAR HOY
              </a>
              <p className="text-sm text-[#3A3A3A]/50 font-medium" data-id="element-14">
                Acceso inmediato + materiales imprimibles incluidos
              </p>
            </motion.div>
          </motion.div>
        </div>
      </header>

      {/* DOLOR Y CONEXIÓN EMOCIONAL */}
      <section className="py-16 px-4 bg-white border-b border-[#E8CFCF]/30" data-id="element-15">
        <div className="max-w-4xl mx-auto" data-id="element-16">
          <h2 className="text-3xl md:text-4xl font-bold text-[#3A3A3A] mb-4 text-center uppercase tracking-wide" data-id="element-17">
            ¿TE SIENTES IDENTIFICADA?
          </h2>
          <p className="text-[#3A3A3A]/60 text-lg text-center mb-8" data-id="element-18">
            Si alguna de estas situaciones te suena familiar, este material es
            para ti:
          </p>
          <div className="grid md:grid-cols-2 gap-4 mb-8" data-id="element-19">
            {['GRITAS AUNQUE PROMETISTE NO HACERLO', 'TE SIENTES CULPABLE DESPUÉS DE CADA EPISODIO', 'TU HIJO HACE BERRINCHES Y NO SABES CÓMO REACCIONAR', 'SIENTES QUE ESTÁS FALLANDO COMO MAMÁ'].map((item, i) => <div key={i} className="flex items-start gap-3 p-5 rounded-xl bg-[#F7F3EF] border border-[#E8CFCF]/50" data-id="element-20">
                <span className="text-[#D88C7A] text-xl mt-0.5" data-id="element-21">✗</span>
                <p className="text-[#3A3A3A]/80 font-semibold" data-id="element-22">{item}</p>
              </div>)}
          </div>
          <div className="bg-[#F5EAEA] p-6 rounded-2xl border border-[#E8CFCF] text-center max-w-2xl mx-auto" data-id="element-23">
            <p className="text-[#3A3A3A] font-bold text-xl" data-id="element-24">
              👉 NO ERES LA ÚNICA. PERO SÍ NECESITAS HERRAMIENTAS.
            </p>
          </div>
        </div>
      </section>

      {/* ROMPER CREENCIA */}
      <section className="py-16 px-4 bg-[#EDF1EA] border-b border-[#A8B5A2]/20" data-id="element-25">
        <div className="max-w-3xl mx-auto text-center" data-id="element-26">
          <p className="text-2xl md:text-4xl text-[#3A3A3A] font-bold leading-tight mb-6 uppercase tracking-wide" data-id="element-27">
            NO NECESITAS SER MÁS PACIENTE.
            <br data-id="element-28" />
            NO NECESITAS SER PERFECTA.
          </p>
          <p className="text-2xl md:text-3xl text-[#D88C7A] font-bold leading-tight uppercase" data-id="element-29">
            NECESITAS SABER QUÉ HACER EN EL MOMENTO EXACTO EN QUE TODO SE
            DESCONTROLA.
          </p>
        </div>
      </section>

      {/* PROBLEM AWARENESS */}
      <section className="py-20 px-4 bg-white" data-id="element-30">
        <div className="max-w-5xl mx-auto" data-id="element-31">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-32">
            <div className="order-2 md:order-1" data-id="element-33">
              <h2 className="text-3xl font-bold text-[#3A3A3A] mb-6 uppercase tracking-wide" data-id="element-34">
                ESTO NO ES OTRA GUÍA MÁS.
              </h2>
              <p className="text-[#D88C7A] text-lg font-bold mb-6 uppercase" data-id="element-35">
                ES LA HERRAMIENTA QUE VAS A USAR CUANDO TODO SE DESCONTROLE:
              </p>
              <div className="space-y-4" data-id="element-36">
                {['Qué decir cuando tu hijo no escucha', 'Cómo manejar rabietas sin gritar', 'Cómo mantener la calma en momentos difíciles', 'Cómo dejar de sentir culpa después'].map((item, i) => <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-[#F5EAEA] border border-[#E8CFCF]" data-id="element-37">
                    <div className="bg-[#E8CFCF] text-[#D88C7A] p-1 rounded-full mt-0.5" data-id="element-38">
                      <CheckIcon className="w-4 h-4" data-id="element-39" />
                    </div>
                    <p className="text-[#3A3A3A]/80" data-id="element-40">{item}</p>
                  </div>)}
              </div>
              <div className="mt-8 p-6 bg-[#EDF1EA] rounded-xl border border-[#A8B5A2]/30" data-id="element-41">
                <p className="text-[#3A3A3A] font-medium" data-id="element-42">
                  👉 Todo explicado de forma simple y aplicable en tu día a día
                </p>
              </div>
            </div>
            <div className="order-1 md:order-2 flex justify-center" data-id="element-43">
              <div className="relative w-full max-w-md aspect-[3/4] bg-[#F5EAEA] rounded-2xl overflow-hidden shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-44">
                <img src="https://cdn.magicpatterns.com/uploads/rit46wTNsnyXQQHNnMTmkB/IMG_1743.png" alt="Guía de Crianza con Inteligencia Emocional" className="w-full h-full object-cover" data-id="element-45" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN PRODUCT SHOWCASE */}
      <section className="py-20 px-4 bg-[#EDF1EA]" data-id="element-46">
        <div className="max-w-6xl mx-auto" data-id="element-47">
          <div className="text-center mb-16" data-id="element-48">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3A3A3A] mb-4 uppercase tracking-wide" data-id="element-49">
              ESTO ES LO QUE VA A CAMBIAR EN TU DÍA A DÍA COMO MAMÁ
            </h2>
            <p className="text-[#3A3A3A]/70 max-w-3xl mx-auto text-lg mb-4" data-id="element-50">
              HOY TU REALIDAD PUEDE VERSE ASÍ:
            </p>
            <p className="text-[#3A3A3A]/80 max-w-3xl mx-auto text-lg font-medium mb-4 uppercase" data-id="element-51">
              GRITAS… TE FRUSTRAS… Y DESPUÉS TE SIENTES CULPABLE.
              <br data-id="element-52" />
              SIENTES QUE LO ESTÁS INTENTANDO… PERO NO ES SUFICIENTE.
            </p>
            <p className="text-[#D88C7A] max-w-3xl mx-auto text-lg font-bold uppercase" data-id="element-53">
              CON ESTA GUÍA, ESO EMPIEZA A CAMBIAR DESDE EL PRIMER DÍA.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-[#A8B5A2]/20 flex flex-col md:flex-row gap-12 items-center" data-id="element-54">
            <div className="w-full md:w-1/2" data-id="element-55">
              <img src="https://cdn.magicpatterns.com/uploads/962ghSQKF3z4ygCbm9XJm1/IMG_1743.png" alt="Portada de la Guía de Crianza" className="w-full rounded-xl shadow-lg transform md:-rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-56" />
            </div>

            <div className="w-full md:w-1/2 space-y-6" data-id="element-57">
              <ul className="space-y-4" data-id="element-58">
                {['SABRÁS EXACTAMENTE QUÉ HACER CUANDO TU HIJO NO ESCUCHA', 'PODRÁS MANEJAR RABIETAS SIN PERDER EL CONTROL', 'TENDRÁS HERRAMIENTAS REALES PARA MOMENTOS DIFÍCILES', 'DEJARÁS DE SENTIR CULPA DESPUÉS DE REACCIONAR'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-59">
                    <div className="bg-[#EDF1EA] text-[#A8B5A2] p-1 rounded-full mt-1" data-id="element-60">
                      <CheckIcon className="w-3 h-3" data-id="element-61" />
                    </div>
                    <span className="text-[#3A3A3A]/80 text-lg font-semibold" data-id="element-62">
                      {item}
                    </span>
                  </li>)}
              </ul>
              <p className="text-[#D88C7A] text-lg font-bold mb-6 uppercase" data-id="element-63">
                👉 NO MÁS IMPROVISAR. NO MÁS SENTIRTE PERDIDA.
              </p>
              <div className="pt-2" data-id="element-64">
                <a href="https://crianza.xn--amordemam-81a.com/products/guia-de-crianza-con-inteligencia-emocional" target="_blank" rel="noopener noreferrer" className="block w-full bg-[#D88C7A] hover:bg-[#C57665] text-white font-bold py-4 px-8 rounded-xl shadow-lg transition-all hover:scale-105 text-center" data-id="element-65">
                  QUIERO SENTIRME SEGURA COMO MAMÁ
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="py-20 px-4 bg-[#F7F3EF] border-y border-[#E8CFCF]/30" data-id="element-66">
        <div className="max-w-6xl mx-auto" data-id="element-67">
          <div className="text-center mb-16" data-id="element-68">
            <span className="text-[#D88C7A] font-bold tracking-wider text-sm uppercase mb-2 block" data-id="element-69">
              Historias Reales
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#3A3A3A] mb-4" data-id="element-70">
              Lo que otras mamás sienten después de empezar
            </h2>
            <div className="flex justify-center gap-1 text-amber-400 mb-2" data-id="element-71">
              <Star className="w-6 h-6 fill-current" data-id="element-72" />
            </div>
            <p className="text-[#3A3A3A]/60" data-id="element-73">Más de 500 mamás felices</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-74">
            {/* Testimonio 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-[#E8CFCF]/40 relative" data-id="element-75">
              <div className="absolute -top-4 left-8 bg-[#F5EAEA] text-[#D88C7A] p-2 rounded-lg" data-id="element-76">
                <span className="text-2xl" data-id="element-77">❝</span>
              </div>
              <div className="mb-4 pt-4" data-id="element-78">
                <div className="flex gap-1 text-amber-400 mb-2" data-id="element-79">
                  <Star className="w-4 h-4 fill-current" data-id="element-80" />
                </div>
                <h3 className="font-bold text-[#3A3A3A] text-lg" data-id="element-81">
                  Transformación emocional
                </h3>
              </div>
              <p className="text-[#3A3A3A]/70 mb-6 italic leading-relaxed" data-id="element-82">
                "Esta guía llegó justo cuando sentía que estaba fallando como
                mamá. Aprendí a escuchar a mis hijos sin gritar y a manejar mis
                propias emociones. Hoy mi casa se siente más tranquila y
                conectada. No es solo una guía, es un abrazo para el alma."
              </p>
              <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-4" data-id="element-83">
                <div className="w-10 h-10 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold" data-id="element-84">
                  MG
                </div>
                <div data-id="element-85">
                  <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-86">Mariana G.</p>
                  <p className="text-[#3A3A3A]/50 text-xs" data-id="element-87">Mamá de 2 niños</p>
                </div>
              </div>
            </motion.div>

            {/* Testimonio 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-[#E8CFCF]/40 relative" data-id="element-88">
              <div className="absolute -top-4 left-8 bg-[#F5EAEA] text-[#D88C7A] p-2 rounded-lg" data-id="element-89">
                <span className="text-2xl" data-id="element-90">❝</span>
              </div>
              <div className="mb-4 pt-4" data-id="element-91">
                <div className="flex gap-1 text-amber-400 mb-2" data-id="element-92">
                  <Star className="w-4 h-4 fill-current" data-id="element-93" />
                </div>
                <h3 className="font-bold text-[#3A3A3A] text-lg" data-id="element-94">
                  Resultados prácticos
                </h3>
              </div>
              <p className="text-[#3A3A3A]/70 mb-6 italic leading-relaxed" data-id="element-95">
                "Lo que más me gustó es que no es teoría complicada. Son
                palabras exactas para usar en momentos difíciles. Las tarjetas
                emocionales cambiaron por completo la forma en que mis hijos
                expresan lo que sienten."
              </p>
              <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-4" data-id="element-96">
                <div className="w-10 h-10 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold" data-id="element-97">
                  LP
                </div>
                <div data-id="element-98">
                  <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-99">Laura P.</p>
                  <p className="text-[#3A3A3A]/50 text-xs" data-id="element-100">Mamá y educadora</p>
                </div>
              </div>
            </motion.div>

            {/* Testimonio 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-[#E8CFCF]/40 relative" data-id="element-101">
              <div className="absolute -top-4 left-8 bg-[#F5EAEA] text-[#D88C7A] p-2 rounded-lg" data-id="element-102">
                <span className="text-2xl" data-id="element-103">❝</span>
              </div>
              <div className="mb-4 pt-4" data-id="element-104">
                <div className="flex gap-1 text-amber-400 mb-2" data-id="element-105">
                  <Star className="w-4 h-4 fill-current" data-id="element-106" />
                </div>
                <h3 className="font-bold text-[#3A3A3A] text-lg" data-id="element-107">
                  Alivio para la mamá
                </h3>
              </div>
              <p className="text-[#3A3A3A]/70 mb-6 italic leading-relaxed" data-id="element-108">
                "Por primera vez sentí que alguien entendía lo agotador que es
                criar con amor. La guía de relajación para mamás fue mi
                salvación. Ahora reacciono desde la calma y no desde el
                cansancio."
              </p>
              <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-4" data-id="element-109">
                <div className="w-10 h-10 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold" data-id="element-110">
                  AR
                </div>
                <div data-id="element-111">
                  <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-112">Andrea R.</p>
                  <p className="text-[#3A3A3A]/50 text-xs" data-id="element-113">Mamá primeriza</p>
                </div>
              </div>
            </motion.div>

            {/* Testimonio 4 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-[#E8CFCF]/40 relative" data-id="element-114">
              <div className="absolute -top-4 left-8 bg-[#F5EAEA] text-[#D88C7A] p-2 rounded-lg" data-id="element-115">
                <span className="text-2xl" data-id="element-116">❝</span>
              </div>
              <div className="mb-4 pt-4" data-id="element-117">
                <div className="flex gap-1 text-amber-400 mb-2" data-id="element-118">
                  <Star className="w-4 h-4 fill-current" data-id="element-119" />
                </div>
                <h3 className="font-bold text-[#3A3A3A] text-lg" data-id="element-120">
                  Confianza y conexión
                </h3>
              </div>
              <p className="text-[#3A3A3A]/70 mb-6 italic leading-relaxed" data-id="element-121">
                "Después de aplicar esta guía, mi hijo empezó a hablar de sus
                emociones sin miedo. Yo también aprendí a perdonarme y a confiar
                en mi proceso como mamá. La recomiendo a todas."
              </p>
              <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-4" data-id="element-122">
                <div className="w-10 h-10 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold" data-id="element-123">
                  CM
                </div>
                <div data-id="element-124">
                  <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-125">
                    Carolina M.
                  </p>
                  <p className="text-[#3A3A3A]/50 text-xs" data-id="element-126">
                    Mamá de un niño sensible
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* DOLOR + CONSECUENCIA */}
      <section className="py-20 px-4 bg-[#F5EAEA]" data-id="element-127">
        <div className="max-w-3xl mx-auto text-center" data-id="element-128">
          <h2 className="text-3xl md:text-4xl font-bold text-[#3A3A3A] mb-8 uppercase tracking-wide" data-id="element-129">
            SI NO HACES NADA… TODO SEGUIRÁ IGUAL.
          </h2>
          <p className="text-xl md:text-2xl text-[#3A3A3A]/80 leading-relaxed mb-8" data-id="element-130">
            Más gritos. Más culpa. Más frustración.
          </p>
          <p className="text-xl text-[#D88C7A] font-bold" data-id="element-131">
            Y eso no es lo que quieres para tu hijo… ni para ti.
          </p>
        </div>
      </section>

      {/* BONUSES */}
      <section className="py-20 px-4 bg-white" data-id="element-132">
        <div className="max-w-6xl mx-auto" data-id="element-133">
          <div className="text-center mb-12" data-id="element-134">
            <span className="bg-gradient-to-r from-[#D88C7A] to-[#C57665] text-white px-8 py-3 rounded-full text-lg md:text-xl font-bold mb-6 inline-block shadow-xl shadow-[#D88C7A]/40 uppercase tracking-widest" data-id="element-135">
              🎁 REGALOS EXCLUSIVOS
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#3A3A3A] uppercase tracking-wide" data-id="element-136">
              LLÉVATE ESTOS 3 BONOS COMPLETAMENTE GRATIS — ¡SOLO POR HOY!
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-137">
            <BonusCard title="Tarjetas Emocionales Ilustradas" value="$35" icon="🎨" description="💡 Perfectas para el hogar, rutinas y momentos difíciles." imageUrl="https://cdn.magicpatterns.com/uploads/urr9uCPGXFn9ek73skaMbG/IMG_0180.jpg" benefits={['Identificar lo que sienten', 'Expresar emociones sin berrinches', 'Desarrollar inteligencia emocional desde pequeños']} delay={0.1} data-id="element-138" />
            <BonusCard title="Cómo Crear Tus Propias Tarjetas Emocionales en Casa" value="$47" icon="✂️" description="✨ Una habilidad que podrás usar para siempre." imageUrl="https://cdn.magicpatterns.com/uploads/tDs3nGtTJ1vuHJUsdaKjBf/IMG_0181.jpg" benefits={['Diseñar tarjetas personalizadas según la edad de tus hijos', 'Adaptarlas a situaciones reales del día a día', 'Convertirlas en una herramienta de conexión familiar']} delay={0.2} data-id="element-139" />
            <BonusCard title="Relajación Emocional para Mamás" value="$55" icon="🧘‍♀️" description="🌿 Diseñado para mamás reales, con poco tiempo." imageUrl="https://cdn.magicpatterns.com/uploads/kkUVLS6SFZZtWuAbAjNMKE/IMG_0182.jpg" benefits={['Ejercicios de descarga emocional', 'Técnicas de calma después de días difíciles', 'Prácticas cortas para recuperar energía mental']} delay={0.3} data-id="element-140" />
          </div>
        </div>
      </section>

      {/* PRICING / CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#F7F3EF] via-[#F5EAEA] to-[#EDF1EA] text-[#3A3A3A] relative overflow-hidden" data-id="element-141">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#D88C7A_1px,transparent_1px)] [background-size:20px_20px]" data-id="element-142"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-143">
          <span className="bg-gradient-to-r from-[#D88C7A] to-[#C57665] text-white px-8 py-3 rounded-full text-sm font-bold mb-8 inline-block shadow-xl shadow-[#D88C7A]/40 uppercase tracking-widest" data-id="element-144">
            🔥 OFERTA POR TIEMPO LIMITADO
          </span>
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-[#3A3A3A] uppercase tracking-wide leading-tight" data-id="element-145">
            HOY DECIDES DEJAR DE GRITAR Y EMPEZAR A CRIAR CON CALMA
          </h2>
          <p className="text-[#3A3A3A]/80 text-xl md:text-2xl mb-4 max-w-2xl mx-auto font-medium uppercase" data-id="element-146">
            TODO LO QUE NECESITAS PARA MANEJAR BERRINCHES SIN CULPA.
          </p>
          <p className="text-[#D88C7A] text-lg font-bold mb-12 max-w-2xl mx-auto" data-id="element-147">
            👉 ACCESO INMEDIATO + MATERIALES IMPRIMIBLES + BONOS GRATIS
          </p>

          <div className="bg-white text-[#3A3A3A] rounded-3xl p-8 md:p-12 max-w-lg mx-auto shadow-2xl border border-[#E8CFCF]/40" data-id="element-148">
            <div className="flex justify-center items-baseline gap-2 mb-2" data-id="element-149">
              <span className="text-5xl font-bold text-[#D88C7A]" data-id="element-150">$12.75</span>
              <span className="text-[#3A3A3A]/40 line-through text-xl" data-id="element-151">
                $137
              </span>
            </div>
            <p className="text-[#D88C7A] mb-8 font-bold uppercase" data-id="element-152">
              ⚡ ESTA OFERTA PUEDE DESAPARECER EN CUALQUIER MOMENTO
            </p>

            <ul className="text-left space-y-3 mb-8 max-w-xs mx-auto" data-id="element-153">
              <li className="flex items-center gap-3" data-id="element-154">
                <CheckIcon className="w-5 h-5 text-[#A8B5A2]" data-id="element-155" />
                <span data-id="element-156">Guía Digital Completa (PDF)</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-157">
                <CheckIcon className="w-5 h-5 text-[#A8B5A2]" data-id="element-158" />
                <span data-id="element-159">Bono 1-Tarjetas Emocionales Ilustradas</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-160">
                <CheckIcon className="w-5 h-5 text-[#A8B5A2]" data-id="element-161" />
                <span data-id="element-162">
                  Bono 2-Cómo Crear Tus Propias Tarjetas Emocionales en Casa
                </span>
              </li>
              <li className="flex items-center gap-3" data-id="element-163">
                <CheckIcon className="w-5 h-5 text-[#A8B5A2]" data-id="element-164" />
                <span data-id="element-165">Bono 3-Relajación Emocional para Mamás</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-166">
                <CheckIcon className="w-5 h-5 text-[#A8B5A2]" data-id="element-167" />
                <span data-id="element-168">Acceso de por vida</span>
              </li>
            </ul>

            <a href="https://crianza.xn--amordemam-81a.com/products/guia-de-crianza-con-inteligencia-emocional" target="_blank" rel="noopener noreferrer" className="block w-full bg-[#D88C7A] hover:bg-[#C57665] text-white font-bold text-xl py-4 px-8 rounded-xl shadow-lg transition-all transform hover:scale-105 text-center" data-id="element-169">
              🔥 QUIERO EMPEZAR HOY — DESCARGAR TODO AHORA
            </a>
            <p className="mt-4 text-xs text-[#3A3A3A]/40" data-id="element-170">
              🔒 Pago 100% seguro • Acceso de por vida • Guía Digital en formato
              PDF 💛
            </p>
          </div>
        </div>
      </section>

      {/* PREMIUM UPGRADE SECTION */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#F7F3EF] to-[#F5EAEA] border-t-4 border-[#D88C7A]/30" data-id="element-171">
        <div className="max-w-4xl mx-auto" data-id="element-172">
          <div className="text-center mb-12" data-id="element-173">
            <a href="https://crianza.xn--amordemam-81a.com/products/guia-de-crianza-con-inteligencia-emocional-copia" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-[#D88C7A] to-[#C57665] text-white px-10 py-4 rounded-full text-lg md:text-xl font-bold mb-8 inline-flex items-center gap-3 shadow-2xl shadow-[#D88C7A]/50 uppercase tracking-widest animate-pulse hover:shadow-3xl hover:scale-105 transition-all" data-id="element-174">
              ⭐ LA OPCIÓN MÁS ELEGIDA — ACCESO PREMIUM COMPLETO
            </a>
            <div className="mt-8 mb-10 flex justify-center" data-id="element-175">
              <img src="https://cdn.magicpatterns.com/uploads/ckwZ9hJYNaT3pvKqLNDuWp/IMG_0282.jpg" alt="Técnicas para Regular las Molestias de tu Hijo - Versión Premium" className="w-full max-w-2xl rounded-2xl shadow-xl" data-id="element-176" />
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-[#3A3A3A] mb-6 leading-tight uppercase tracking-wide" data-id="element-177">
              ESTO ES LO QUE REALMENTE VAS A USAR CUANDO TU HIJO ESTÉ EN CRISIS
            </h2>
            <p className="text-[#3A3A3A]/80 text-xl md:text-2xl max-w-3xl mx-auto mb-4 font-medium uppercase" data-id="element-178">
              EL SISTEMA PRÁCTICO QUE TODA MAMÁ NECESITA TENER A LA MANO
            </p>
            <p className="text-[#D88C7A] text-lg md:text-xl max-w-3xl mx-auto mb-6 font-bold" data-id="element-179">
              ✨ TÉCNICAS PARA REGULAR LOS MOMENTOS DE MOLESTIA DE TU HIJO —
              INCLUIDAS SOLO EN ESTA VERSIÓN
            </p>
          </div>

          {/* QUÉ INCLUYE */}
          <div className="bg-white rounded-3xl p-8 md:p-10 mb-12 border-4 border-[#D88C7A]/20 shadow-2xl" data-id="element-180">
            <h3 className="text-2xl md:text-3xl font-bold text-[#3A3A3A] mb-8 text-center uppercase tracking-wide" data-id="element-181">
              ✨ TODO ESTO ESTÁ INCLUIDO:
            </h3>
            <div className="grid md:grid-cols-2 gap-5" data-id="element-182">
              {['TARJETAS LISTAS PARA USAR EN EL MOMENTO', 'FRASES QUE FUNCIONAN EN LA VIDA REAL', 'JUEGOS EMOCIONALES PARA CALMAR A TU HIJO', 'HERRAMIENTAS PROBADAS PARA EVITAR GRITOS'].map((item, i) => <div key={i} className="flex items-start gap-3 bg-[#EDF1EA]/50 p-4 rounded-xl" data-id="element-183">
                  <CheckIcon className="w-6 h-6 text-[#A8B5A2] flex-shrink-0 mt-0.5" data-id="element-184" />
                  <span className="text-[#3A3A3A]/80 font-bold" data-id="element-185">{item}</span>
                </div>)}
            </div>
            <div className="mt-8 p-6 bg-[#F5EAEA] rounded-2xl border-2 border-[#D88C7A]/30" data-id="element-186">
              <p className="text-[#D88C7A] font-bold text-xl text-center uppercase" data-id="element-187">
                🔥 NO ES TEORÍA. ES ACCIÓN INMEDIATA PARA TU DÍA A DÍA.
              </p>
            </div>
          </div>

          {/* BLOQUE DESTACADO - BONO PREMIUM */}
          <div className="bg-[#F5EAEA] rounded-3xl p-8 md:p-10 mb-12 border-2 border-[#E8CFCF] relative overflow-hidden shadow-lg" data-id="element-188">
            <div className="absolute top-0 right-0 bg-[#D88C7A] text-white px-6 py-2 rounded-bl-2xl font-bold text-sm" data-id="element-189">
              NUEVO BONO PREMIUM EXCLUSIVO
            </div>
            <div className="mt-6 md:mt-2 flex flex-col md:flex-row gap-8 items-center" data-id="element-190">
              <div className="w-full md:w-2/5 flex-shrink-0" data-id="element-191">
                <img src="https://cdn.magicpatterns.com/uploads/nM4jdonaD8gYfAZbFuhLAh/IMG_1737.jpg" alt="Técnicas para Regular las Molestias de tu Hijo" className="w-full rounded-2xl shadow-lg" data-id="element-192" />
              </div>
              <div className="w-full md:w-3/5" data-id="element-193">
                <h3 className="text-2xl md:text-3xl font-bold text-[#3A3A3A] mb-4" data-id="element-194">
                  Técnicas para Regular las Molestias de tu Hijo
                </h3>
                <p className="text-[#3A3A3A]/80 text-lg mb-8" data-id="element-195">
                  Un sistema práctico de tarjetas imprimibles diseñado para
                  ayudarte a actuar con seguridad en momentos de rabietas,
                  frustración o llanto.
                </p>

                <div className="space-y-4 mb-8" data-id="element-196">
                  {['Frases exactas que puedes decir en momentos difíciles', 'Técnicas efectivas para calmar sin gritar ni castigar', 'Estrategias para acompañar emociones intensas', 'Herramientas visuales fáciles de usar con tu hijo'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-197">
                      <div className="bg-[#E8CFCF] text-[#D88C7A] p-1 rounded-full mt-0.5 flex-shrink-0" data-id="element-198">
                        <CheckIcon className="w-4 h-4" data-id="element-199" />
                      </div>
                      <span className="text-[#3A3A3A]/80" data-id="element-200">{item}</span>
                    </div>)}
                </div>

                <div className="bg-white/60 rounded-xl p-6 border border-[#E8CFCF]/50" data-id="element-201">
                  <p className="text-[#D88C7A] font-bold text-lg mb-2" data-id="element-202">
                    👉 Ya no tendrás que improvisar
                  </p>
                  <p className="text-[#D88C7A] font-bold text-lg" data-id="element-203">
                    👉 Tendrás claridad en cada situación
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* BLOQUE EMOCIONAL */}
          <div className="text-center mb-12 max-w-3xl mx-auto" data-id="element-204">
            <p className="text-2xl md:text-3xl text-[#3A3A3A] font-bold mb-8 uppercase tracking-wide" data-id="element-205">
              SABEMOS LO DIFÍCIL QUE ES CUANDO TU HIJO:
            </p>
            <div className="space-y-4 mb-8" data-id="element-206">
              {['😤 TIENE UNA RABIETA INESPERADA Y NO SABES CÓMO REACCIONAR', '😢 LLORA SIN PARAR Y NADA DE LO QUE HACES FUNCIONA', '😣 SE FRUSTRA Y NO SABE CÓMO EXPRESAR LO QUE SIENTE'].map((item, i) => <div key={i} className="bg-[#F5EAEA] p-5 rounded-xl border border-[#E8CFCF]/50" data-id="element-207">
                  <p className="text-[#3A3A3A]/80 text-lg font-bold" data-id="element-208">{item}</p>
                </div>)}
            </div>
            <p className="text-xl md:text-2xl text-[#3A3A3A]/80 font-medium mb-6 uppercase" data-id="element-209">
              TÚ SOLO QUIERES AYUDAR… PERO NO SABES EXACTAMENTE CÓMO HACERLO.
            </p>
            <p className="text-xl md:text-2xl font-bold text-[#3A3A3A] mb-4 uppercase" data-id="element-210">
              ¡ESO SE ACABA HOY!
            </p>
            <p className="text-2xl md:text-3xl font-bold text-[#D88C7A] mb-8 uppercase" data-id="element-211">
              💛 ESTE BONO FUE CREADO EXACTAMENTE PARA ESOS MOMENTOS REALES.
            </p>
            <div className="bg-gradient-to-r from-[#D88C7A]/10 to-[#C57665]/10 p-8 rounded-2xl border-2 border-[#D88C7A]/30 shadow-lg" data-id="element-212">
              <p className="text-[#3A3A3A] font-bold text-xl md:text-2xl text-center uppercase tracking-wide leading-relaxed" data-id="element-213">
                ESTA VERSIÓN PREMIUM NO SOLO TE ENSEÑA…
              </p>
              <p className="text-[#D88C7A] font-bold text-2xl md:text-3xl text-center uppercase tracking-wide mt-3" data-id="element-214">
                🔥 TE GUÍA PASO A PASO EN EL MOMENTO EXACTO DONDE TODO OCURRE.
              </p>
            </div>
          </div>

          {/* PRICING PREMIUM */}
          <div className="bg-[#F7F3EF] text-[#3A3A3A] rounded-3xl p-8 md:p-12 max-w-lg mx-auto shadow-2xl border-2 border-[#D88C7A]/30 text-center relative" data-id="element-215">
            <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-[#D88C7A] text-white px-6 py-2 rounded-full font-bold shadow-md w-max" data-id="element-216">
              ACCESO PREMIUM COMPLETO
            </div>

            <p className="text-[#3A3A3A]/70 mt-4 mb-6 font-medium" data-id="element-217">
              Todo el contenido + bono exclusivo
            </p>

            <div className="flex justify-center items-baseline gap-2 mb-8" data-id="element-218">
              <span className="text-6xl font-bold text-[#D88C7A]" data-id="element-219">$15.99</span>
              <span className="text-xl text-[#3A3A3A]/50" data-id="element-220">USD</span>
            </div>

            <a href="https://crianza.xn--amordemam-81a.com/products/guia-de-crianza-con-inteligencia-emocional-copia" target="_blank" rel="noopener noreferrer" className="block w-full bg-[#D88C7A] hover:bg-[#C57665] text-white font-bold text-xl py-4 px-8 rounded-xl shadow-lg transition-all transform hover:scale-105 text-center mb-6" data-id="element-221">
              QUIERO LA VERSIÓN PREMIUM AHORA
            </a>

            <p className="text-sm text-[#3A3A3A]/60 font-medium mb-4" data-id="element-222">
              ⚠️ Empieza hoy mismo a transformar la forma en que manejas los
              momentos difíciles en casa.
            </p>

            <div className="border-t border-[#E8CFCF]/40 pt-4 mt-4" data-id="element-223">
              <p className="text-sm text-[#D88C7A] font-bold italic text-center" data-id="element-224">
                💛 Ideal para mamás que quieren saber exactamente qué hacer en
                momentos difíciles.
              </p>
            </div>
          </div>

          {/* TRANSFORMACIÓN */}
          <div className="bg-white rounded-3xl p-8 md:p-10 mb-16 border border-[#E8CFCF]/40 shadow-lg" data-id="element-225">
            <h3 className="text-2xl md:text-3xl font-bold text-[#3A3A3A] mb-6 text-center" data-id="element-226">
              Imagina esto:
            </h3>
            <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto mb-8" data-id="element-227">
              {['Mantener la calma aunque tu hijo esté alterado', 'Saber exactamente qué hacer en cada situación', 'Sentirte segura como mamá', 'Tener una relación más tranquila con tu hijo'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-228">
                  <CheckIcon className="w-5 h-5 text-[#A8B5A2] flex-shrink-0 mt-1" data-id="element-229" />
                  <span className="text-[#3A3A3A]/80 font-medium" data-id="element-230">{item}</span>
                </div>)}
            </div>
            <p className="text-[#D88C7A] font-bold text-lg text-center" data-id="element-231">
              👉 Eso es lo que empiezas a construir desde hoy.
            </p>
          </div>

          {/* TESTIMONIOS PREMIUM */}
          <div className="mt-16" data-id="element-232">
            <h3 className="text-2xl font-bold text-[#3A3A3A] text-center mb-10" data-id="element-233">
              💬 Lo que dicen las mamás sobre el plan premium
            </h3>

            {/* Testimonio destacado */}
            <div className="bg-[#F5EAEA] border-2 border-[#D88C7A]/30 rounded-3xl p-8 md:p-10 mb-8 shadow-lg relative" data-id="element-234">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#D88C7A] text-white px-5 py-1.5 rounded-full font-bold text-sm shadow-md" data-id="element-235">
                ⭐ TESTIMONIO DESTACADO
              </div>
              <p className="text-[#3A3A3A]/80 text-lg italic leading-relaxed mb-4 mt-2" data-id="element-236">
                "Este fue el bono que realmente hizo la diferencia. El ebook me
                ayudó a entender, pero estas tarjetas me dieron herramientas
                reales para el día a día. Ahora cuando mi hijo se frustra, ya sé
                qué hacer."
              </p>
              <div className="flex items-center gap-3" data-id="element-237">
                <div className="w-10 h-10 bg-[#E8CFCF] rounded-full flex items-center justify-center text-[#D88C7A] font-bold" data-id="element-238">
                  C
                </div>
                <div data-id="element-239">
                  <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-240">Carolina</p>
                  <p className="text-[#3A3A3A]/50 text-xs" data-id="element-241">
                    Mamá de niño de 2 años
                  </p>
                </div>
              </div>
            </div>

            {/* Otros testimonios */}
            <div className="grid md:grid-cols-2 gap-6" data-id="element-242">
              <div className="bg-white p-6 rounded-2xl border border-[#E8CFCF]/40 shadow-sm" data-id="element-243">
                <p className="text-[#3A3A3A]/70 italic leading-relaxed mb-4" data-id="element-244">
                  "Las tarjetas son lo mejor que he comprado para mi hijo. Son
                  tan fáciles de usar que incluso en medio de una rabieta puedo
                  aplicarlas. Se siente todo mucho más tranquilo en casa."
                </p>
                <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-3" data-id="element-245">
                  <div className="w-9 h-9 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold text-sm" data-id="element-246">
                    V
                  </div>
                  <div data-id="element-247">
                    <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-248">Valeria</p>
                    <p className="text-[#3A3A3A]/50 text-xs" data-id="element-249">
                      Mamá de niño de 3 años
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E8CFCF]/40 shadow-sm" data-id="element-250">
                <p className="text-[#3A3A3A]/70 italic leading-relaxed mb-4" data-id="element-251">
                  "Por fin algo práctico y no solo teoría. Me encantó porque no
                  tengo que pensar qué hacer… simplemente uso las técnicas y
                  funciona."
                </p>
                <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-3" data-id="element-252">
                  <div className="w-9 h-9 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold text-sm" data-id="element-253">
                    D
                  </div>
                  <div data-id="element-254">
                    <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-255">Daniela</p>
                    <p className="text-[#3A3A3A]/50 text-xs" data-id="element-256">
                      Mamá de niño de 4 años
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E8CFCF]/40 shadow-sm" data-id="element-257">
                <p className="text-[#3A3A3A]/70 italic leading-relaxed mb-4" data-id="element-258">
                  "Mi hijo ahora logra calmarse más rápido. Antes todo terminaba
                  en llanto, ahora con estas técnicas puedo ayudarlo a
                  regularse."
                </p>
                <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-3" data-id="element-259">
                  <div className="w-9 h-9 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold text-sm" data-id="element-260">
                    L
                  </div>
                  <div data-id="element-261">
                    <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-262">Laura</p>
                    <p className="text-[#3A3A3A]/50 text-xs" data-id="element-263">
                      Mamá de niño de 2 años
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E8CFCF]/40 shadow-sm" data-id="element-264">
                <p className="text-[#3A3A3A]/70 italic leading-relaxed mb-4" data-id="element-265">
                  "Vale completamente la pena el plan premium. Las tarjetas y
                  las técnicas me dieron seguridad como mamá. Es justo lo que
                  necesitaba para dejar de sentirme perdida."
                </p>
                <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-3" data-id="element-266">
                  <div className="w-9 h-9 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold text-sm" data-id="element-267">
                    A
                  </div>
                  <div data-id="element-268">
                    <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-269">Andrea</p>
                    <p className="text-[#3A3A3A]/50 text-xs" data-id="element-270">
                      Mamá de niño de 3 años
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA FINAL PREMIUM */}
            <div className="mt-12 text-center bg-white rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-[#D88C7A]/30" data-id="element-271">
              <p className="text-xl md:text-2xl text-[#3A3A3A] font-bold mb-2 uppercase tracking-wide" data-id="element-272">
                🔥 ACCESO PREMIUM COMPLETO POR SOLO
              </p>
              <p className="text-6xl md:text-7xl font-bold text-[#D88C7A] mb-2" data-id="element-273">
                $15.99 <span className="text-xl text-[#3A3A3A]/50" data-id="element-274">USD</span>
              </p>
              <p className="text-[#D88C7A] font-bold mb-8 uppercase" data-id="element-275">
                ⚡ ESTE PRECIO NO ESTARÁ DISPONIBLE POR MUCHO TIEMPO
              </p>
              <a href="https://crianza.xn--amordemam-81a.com/products/guia-de-crianza-con-inteligencia-emocional-copia" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#D88C7A] hover:bg-[#C57665] text-white font-bold text-xl md:text-2xl py-5 px-12 rounded-xl shadow-lg transition-all transform hover:scale-105 text-center" data-id="element-276">
                🔥 SÍ, QUIERO EL ACCESO PREMIUM AHORA
              </a>
              <p className="mt-4 text-sm text-[#3A3A3A]/50" data-id="element-277">
                🔒 PAGO 100% SEGURO • ACCESO INMEDIATO Y DE POR VIDA 💛
              </p>
              <p className="mt-4 text-lg text-[#D88C7A] font-bold uppercase" data-id="element-278">
                💛 PARA MAMÁS QUE QUIEREN SABER EXACTAMENTE QUÉ HACER EN LOS
                MOMENTOS MÁS DIFÍCILES.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* URGENCIA FINAL */}
      <section className="py-20 px-4 bg-[#F7F3EF]" data-id="element-279">
        <div className="max-w-3xl mx-auto text-center" data-id="element-280">
          <h2 className="text-3xl md:text-5xl font-bold text-[#3A3A3A] mb-8 leading-tight" data-id="element-281">
            Puedes seguir intentando sola… o empezar hoy a hacerlo diferente.
          </h2>
          <p className="text-xl text-[#3A3A3A]/80 leading-relaxed mb-10" data-id="element-282">
            Cada día que pasa es un día más sintiéndote igual.
          </p>
          <a href="https://crianza.xn--amordemam-81a.com/products/guia-de-crianza-con-inteligencia-emocional" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#D88C7A] hover:bg-[#C57665] text-white font-bold text-xl py-4 px-10 rounded-xl shadow-lg transition-all transform hover:scale-105 text-center" data-id="element-283">
            👉 QUIERO EMPEZAR HOY SIN GRITOS NI CULPA
          </a>
          <a href="https://crianza.xn--amordemam-81a.com/products/guia-de-crianza-con-inteligencia-emocional-copia" target="_blank" rel="noopener noreferrer" className="inline-block mt-4 bg-[#D88C7A] hover:bg-[#C57665] text-white font-bold text-xl py-4 px-10 rounded-xl shadow-lg transition-all transform hover:scale-105 text-center" data-id="element-284">
            ⭐ QUIERO TODO EL SISTEMA COMPLETO (GUÍA + TARJETAS)
          </a>
          <p className="mt-2 text-sm text-[#D88C7A] font-bold uppercase" data-id="element-285">
            VERSIÓN PREMIUM
          </p>
        </div>
      </section>

      <Footer data-id="element-286" />
    </div>;
}